import { GA4EcommercePayloadItem, EventValues } from '@sial/common-utils'
import { searchResultsRoute } from '@src/routes'
import {
  ProductSearchType,
  determineQueryFocus,
  getValidSearch,
  formatCasNumberSearchTerm,
} from '@utils/searchUtils'

import { ProductAliasPayload } from './types'
import { CarouselProduct } from '@src/components/DynamicProductCarousel/DynamicProductCardTypes'
import subscriptionFrequencyMessages from '@src/utils/MessageSets/defineSubscriptionFrequencyMessages'

export const buildProductAliasPayload = ({
  action,
  detail,
  focus,
  productId,
  productBrand,
}: {
  action: string
  detail?: string | undefined | null
  focus: string
  productId?: string
  productBrand?: string
}): ProductAliasPayload => {
  let type
  let term
  switch (action) {
    case 'ec number':
      type = ProductSearchType.EgecNumber
      break
    case 'mdl number':
      type = ProductSearchType.MdlNumber
      break
    case 'cas number':
      type = ProductSearchType.CasNumber
      term = formatCasNumberSearchTerm(detail)
      break
    default:
  }
  return {
    action: action + ' click',
    detail: detail,
    component: 'body',
    elementType: 'link',
    elementText: detail,
    linkUrl: `${searchResultsRoute.searchResults(
      term || detail || undefined
    )}${getValidSearch({
      term: term?.toLowerCase() || detail?.toLowerCase(),
      focus: determineQueryFocus(focus),
      type,
    })}`,
    productId,
    productBrand,
  }
}

/**
 * BUILD PROMO OBJECTS FOR CAROUSELS:
 * Could be used elsewhere for product data structures that use the gaProductCode alongside product data.
 * Carousels is the original primary purpose.
 * @param rawGaProductCode / String BE gives with pipe separated promotion data for GA.
 * @param pickedProduct / CarouselProduct type with data on product viewed or clicked in carousel.
 * @param position / number position of where the item was in the carousel left to right.
 * @param listingLocationOverride / string override of page type carousel was displayed on.
 * @param listingLocationDetailOverride / string same as listingLocationOverride but extended detail summary space.
 * @returns GA4EcommercePayloadItem
 */
export const buildGaPromoObject = (
  rawGaProductCode: string | null | undefined,
  pickedProduct: CarouselProduct,
  position: number = 1,
  listingLocationOverride: string | null = null, // Name for the type of page promo is shown on.
  listingLocationDetailOverride: string | null = null
): GA4EcommercePayloadItem => {
  const gaProductCode = rawGaProductCode?.toLowerCase()?.split('|') || []

  const ga4PromoObj: GA4EcommercePayloadItem = {
    promotion_id: gaProductCode[0] || EventValues.NotAvailable,
    promotion_name: gaProductCode[1] || EventValues.NotAvailable,
    creative_name: gaProductCode[2] || EventValues.NotAvailable,
    index: position,
    item_id: pickedProduct.productKey.toLowerCase(),
    item_brand:
      pickedProduct.brand?.key?.toLowerCase() || EventValues.NotAvailable,
    item_list_id:
      listingLocationOverride?.toLowerCase() || EventValues.NotAvailable,
    item_list_name:
      listingLocationDetailOverride?.toLowerCase() ||
      listingLocationOverride?.toLowerCase() ||
      EventValues.NotAvailable,
  }

  return ga4PromoObj
}

export const calculateDiscountPercentage = (
  listPrice,
  discountedPrice
): number | undefined => {
  return (listPrice ?? 0) > (discountedPrice ?? 0)
    ? Math.round(
        (((listPrice ?? 0) - (discountedPrice ?? 0)) / (listPrice ?? 1)) * 100
      )
    : undefined
}
export const getSubscriptionFrequencyLabel = (
  key: string | null | undefined
): string => {
  if (!key) return 'one time order'
  return subscriptionFrequencyMessages[key]?.value ?? 'one time order'
}
